import { SkillCategory } from '@applicator/shared'
import React from 'react'

import CustomCheckbox from '../CustomCheckbox'
import WorkExperienceDragHandle from './WorkExperienceDragHandle'

interface SkillCategoryItemProps {
  category: SkillCategory
  categoryIndex: number
  categories: SkillCategory[]
  editingCategoryId: string | null
  editingCategoryName: string
  editingSkillId: { categoryId: string; skillId: string } | null
  editingSkillName: string
  newSkillInput: { categoryId: string; value: string } | null
  selectedSkills: Set<string>
  isMultiSelectMode: boolean
  draggedSkillId: string | null
  draggedCategoryId: string | null
  dragOverCategoryId: string | null
  draggedCategoryBlockId: string | null
  dragOverCategoryIndex: number | null
  isCategoryBlockDragActive: () => boolean
  onChange: (categories: SkillCategory[]) => void
  onStartEditCategory: (categoryId: string) => void
  onSaveCategory: (categoryId: string) => void
  onDeleteCategory: (categoryId: string) => void
  onAddSkill: (categoryId: string) => void
  onDeleteSkill: (categoryId: string, skillId: string) => void
  onToggleSkill: (categoryId: string, skillId: string) => void
  onToggleSkillSelection: (categoryId: string, skillId: string, e: React.MouseEvent) => void
  onDragStart: (categoryId: string, skillId: string, e: React.DragEvent) => void
  onDragEnd: () => void
  onDragOver: (categoryId: string, e: React.DragEvent) => void
  onDrop: (categoryId: string, e: React.DragEvent) => void
  onDragLeave: (categoryIndex: number) => void
  onCategoryBlockDragStart: (categoryId: string, e: React.DragEvent) => void
  onCategoryBlockDragOver: (categoryIndex: number, e: React.DragEvent) => void
  onCategoryBlockDrop: (categoryIndex: number, e: React.DragEvent) => void
  onCategoryBlockDragEnd: () => void
  setEditingCategoryId: (id: string | null) => void
  setEditingCategoryName: (value: string) => void
  setEditingSkillId: (id: { categoryId: string; skillId: string } | null) => void
  setEditingSkillName: (value: string) => void
  setNewSkillInput: (input: { categoryId: string; value: string } | null) => void
}

export default function SkillCategoryItem({
  category,
  categoryIndex,
  categories,
  editingCategoryId,
  editingCategoryName,
  editingSkillId,
  editingSkillName,
  newSkillInput,
  selectedSkills,
  isMultiSelectMode,
  draggedSkillId,
  draggedCategoryId,
  dragOverCategoryId,
  draggedCategoryBlockId,
  dragOverCategoryIndex,
  isCategoryBlockDragActive,
  onChange,
  onStartEditCategory,
  onSaveCategory,
  onDeleteCategory,
  onAddSkill,
  onDeleteSkill,
  onToggleSkill,
  onToggleSkillSelection,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  onDragLeave,
  onCategoryBlockDragStart,
  onCategoryBlockDragOver,
  onCategoryBlockDrop,
  onCategoryBlockDragEnd,
  setEditingCategoryId,
  setEditingCategoryName,
  setEditingSkillId,
  setEditingSkillName,
  setNewSkillInput,
}: SkillCategoryItemProps) {
  const isCategoryBlockDragging = draggedCategoryBlockId === category.id
  const isCategoryBlockDragOver =
    dragOverCategoryIndex === categoryIndex && draggedCategoryBlockId !== category.id
  const isSkillDropTarget =
    dragOverCategoryId === category.id &&
    draggedCategoryId !== category.id &&
    !draggedCategoryBlockId

  return (
    <div
      key={category.id}
      data-skill-category-id={category.id}
      data-testid="skill-category-block"
      className={`group/skill-cat border border-border-default rounded-lg p-3 bg-surface-default transition-all ${
        isCategoryBlockDragOver ? 'ring-2 ring-interactive-focus-ring ring-inset' : ''
      }`}
      onDragOver={(e) => {
        if (isCategoryBlockDragActive()) {
          onCategoryBlockDragOver(categoryIndex, e)
        } else {
          onDragOver(category.id, e)
        }
      }}
      onDrop={(e) => {
        if (isCategoryBlockDragActive()) {
          onCategoryBlockDrop(categoryIndex, e)
        } else {
          onDrop(category.id, e)
        }
      }}
      onDragLeave={() => onDragLeave(categoryIndex)}
    >
      {/* Category header — draggable block (skills chips drag separately) */}
      <div
        data-testid="skill-category-drag-header"
        draggable
        onDragStart={(e) => onCategoryBlockDragStart(category.id, e)}
        onDragEnd={onCategoryBlockDragEnd}
        className={`flex items-start gap-2 mb-3 cursor-move ${
          isCategoryBlockDragging ? 'opacity-50' : ''
        }`}
      >
        <WorkExperienceDragHandle
          group="skill-cat"
          title="Перетащите группу для изменения порядка"
        />
        <div className="flex items-center justify-between flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {editingCategoryId === category.id ? (
            <input
              type="text"
              value={editingCategoryName}
              onChange={(e) => setEditingCategoryName(e.target.value)}
              onBlur={() => onSaveCategory(category.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  onSaveCategory(category.id)
                } else if (e.key === 'Escape') {
                  e.preventDefault()
                  setEditingCategoryId(null)
                  setEditingCategoryName('')
                }
              }}
              placeholder="Category name"
              className="text-sm font-semibold text-text-primary border border-interactive rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-interactive-focus-ring flex-1"
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
            />
          ) : (
            <h3
              // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
              role="button"
              tabIndex={0}
              onClick={() => onStartEditCategory(category.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onStartEditCategory(category.id)
                }
              }}
              className="text-sm font-semibold text-text-primary cursor-text hover:text-interactive transition-colors"
            >
              {category.name || 'Unnamed Category'}
            </h3>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => onAddSkill(category.id)}
            className="p-1 text-text-muted hover:text-interactive transition-colors"
            title="Add skill"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
          <button
            onClick={() => onDeleteCategory(category.id)}
            className="p-1 text-text-muted hover:text-danger transition-colors"
            title="Delete group"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
        </div>
      </div>

      {/* Add Skill Input */}
      {newSkillInput?.categoryId === category.id && (
        <div className="mb-2">
          <input
            type="text"
            value={newSkillInput.value}
            onChange={(e) => setNewSkillInput({ categoryId: category.id, value: e.target.value })}
            onBlur={() => {
              if (newSkillInput.value.trim()) {
                onAddSkill(category.id)
              } else {
                setNewSkillInput(null)
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newSkillInput.value.trim()) {
                e.preventDefault()
                onAddSkill(category.id)
              } else if (e.key === 'Escape') {
                e.preventDefault()
                setNewSkillInput(null)
              }
            }}
            placeholder="Enter skill names (comma-separated)"
            className="w-full text-sm border border-interactive rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-interactive-focus-ring"
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          />
        </div>
      )}

      {/* Skills Chips */}
      <div className="flex flex-wrap gap-2">
        {(category.skills || []).map((skill) => {
          const skillKey = `${category.id}-${skill.id}`
          const isSelected = selectedSkills.has(skillKey)
          const isDragging = draggedSkillId === skill.id && draggedCategoryId === category.id
          
          return (
            <div
              key={skill.id}
              draggable
              role="button"
              tabIndex={0}
              onDragStart={(e) => onDragStart(category.id, skill.id, e)}
              onDragEnd={onDragEnd}
              onClick={(e) => onToggleSkillSelection(category.id, skill.id, e)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onToggleSkillSelection(category.id, skill.id, e as unknown as React.MouseEvent)
                }
              }}
              className={`
                flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border transition-all cursor-move
                ${skill.included 
                  ? 'bg-surface-default border-interactive text-text-primary' 
                  : 'bg-surface-muted border-border-default text-text-muted'
                }
                ${isSelected ? 'ring-2 ring-interactive-focus-ring ring-offset-1' : ''}
                ${isDragging ? 'opacity-50' : ''}
                ${isMultiSelectMode || isSelected ? 'cursor-pointer' : 'cursor-move'}
                hover:shadow-sm
              `}
            >
              <div 
                role="button"
                tabIndex={0}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    e.stopPropagation()
                    onToggleSkill(category.id, skill.id)
                  }
                }}
              >
                <CustomCheckbox
                  checked={skill.included}
                  onChange={() => onToggleSkill(category.id, skill.id)}
                  size="xs"
                />
              </div>
              {isMultiSelectMode && (
                <div className="w-3 h-3 border-2 border-border-strong rounded flex items-center justify-center">
                  {isSelected && (
                    <svg className="w-2.5 h-2.5 text-interactive" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
              )}
              {editingSkillId?.categoryId === category.id && editingSkillId?.skillId === skill.id ? (
                <input
                  type="text"
                  value={editingSkillName}
                  onChange={(e) => setEditingSkillName(e.target.value)}
                  onBlur={() => {
                    if (editingSkillName.trim()) {
                      // Call parent's save handler
                      const updated = categories.map((cat) => {
                        if (cat.id === category.id) {
                          return {
                            ...cat,
                            skills: (cat.skills || []).map((s) =>
                              s.id === skill.id ? { ...s, name: editingSkillName.trim() } : s
                            ),
                          }
                        }
                        return cat
                      })
                      onChange(updated)
                    }
                    setEditingSkillId(null)
                    setEditingSkillName('')
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      e.currentTarget.blur()
                    } else if (e.key === 'Escape') {
                      e.preventDefault()
                      setEditingSkillId(null)
                      setEditingSkillName('')
                    }
                  }}
                  className="text-xs font-medium border-none outline-none bg-transparent px-0 py-0 min-w-[60px]"
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <span 
                  className="text-xs font-medium"
                  onDoubleClick={(e) => {
                    e.stopPropagation()
                    setEditingSkillId({ categoryId: category.id, skillId: skill.id })
                    setEditingSkillName(skill.name)
                  }}
                >
                  {skill.name}
                </span>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onDeleteSkill(category.id, skill.id)
                }}
                className="ml-1 text-text-muted hover:text-danger transition-colors"
                title="Delete skill"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )
        })}
      </div>

      {/* Drop indicator — skill moved into this category */}
      {isSkillDropTarget && (
        <div className="mt-2 h-0.5 bg-interactive rounded" />
      )}
    </div>
  )
}
